import {useState, useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {collection, doc, onSnapshot} from 'firebase/firestore';
import db from 'services/firebase';
import {
  Integration, 
  filterIntegrations as filterIntegrationsUtil,
  handleOAuthCallback
} from './integrationConfig';

interface UseIntegrationManagementProps {
  currentUser: any;
}

interface UseIntegrationManagementReturn {
  integration: Integration[];
  filteredIntegrations: Integration[];
  isModalOpen: boolean;
  selectedItemId: string;
  activeIntegration: string;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>
  selectedCategory: string;
  handleOpenModal: (itemId: string, integrationType: string) => void;
  handleCloseModal: () => void;
  handleCategoryChange: (category: string) => void;
}

export const useIntegrationManagement = ({
  currentUser
}: UseIntegrationManagementProps): UseIntegrationManagementReturn => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [integration, setIntegration] = useState<Integration[]>([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredIntegrations, setFilteredIntegrations] = useState<Integration[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [activeIntegration, setActiveIntegration] = useState<string>('');
  
  const location = useLocation();
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state') || '';

  useEffect(() => {
    const getIntegrations = () => {
      try {
        const tenantId = currentUser?.tenantId || '';
        if (!tenantId) return;
        
        const tenantRef = doc(db, 'tenants', tenantId);
        const integrationsRef = collection(tenantRef, 'integrations');

        return onSnapshot(integrationsRef, (snapshot) => {
          const integrationData = snapshot.docs.map((item) => ({
            ...item.data(),
            id: item.id,
          })) as Integration[];
          
          setIntegration(integrationData);
        });
      } catch (error) {
        console.error('Error getting integrations:', error);
      }
    };

    const unsubscribe = getIntegrations();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [currentUser]);

  useEffect(() => {
    if (code) {
      handleOAuthCallback(code, state, integration, currentUser, navigate);
    }
  }, [code, integration, currentUser, navigate, state]);

  useEffect(() => {
    const filteredData = filterIntegrationsUtil(integration, searchTerm, selectedCategory);
    setFilteredIntegrations(filteredData);
  }, [integration, searchTerm, selectedCategory]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const id = queryParams.get('id');
    const serviceParam = queryParams.get('service');

    if (id && serviceParam === 'Linear') {
      handleOpenModal(id, 'Linear');
    }
    
    if (id && serviceParam === 'Aikido Security') {
      handleOpenModal(id, 'Aikido Security');
    }
  }, [location]);

  const handleOpenModal = (itemId: string, integrationType: string) => {
    setIsModalOpen(true);
    setSelectedItemId(itemId);
    setActiveIntegration(integrationType);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };


  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
  };

  return {
    integration,
    filteredIntegrations,
    isModalOpen,
    selectedItemId,
    activeIntegration,
    searchTerm,
    setSearchTerm,
    selectedCategory,
    handleOpenModal,
    handleCloseModal,
    handleCategoryChange,
  };
};